import {
  ApiIndividualResponse,
  ApiPaginatedResponse,
  ApiPaginationParams,
  ISortingParams,
} from '@/types/api';
import {
  ApiLearningBudgetTransactionsDigestResponse,
  ILearningBudgetCreatePayload,
  ILearningBudgetSetup,
  ILearningBudgetSetupInTheList,
  ILearningBudgetTransactionFilters,
  ILearningBudgetTransactionInTheList,
  ILearningBudgetUpdatePayload,
} from '@/types/learning-budget';
import qs from 'query-string';
import { ApiService } from './api';

export type UpdateUserBudgetAmountOpts = {
  user: string;
  amount: number;
};

export type UpdateTeamBudgetAmountOpts = {
  team: string;
  amount: number;
};

export type GetLearningBudgetSetupFilters = {
  company: string;
};

export class LearningBudgetService extends ApiService {
  static PATH = '/learning-budgets';
  static TRANSACTIONS_PATH = '/learning-budget-transactions';
  static TRANSACTIONS_EXPORT_PATH = '/learning-budget-transactions/export';
  static SETUP_PATH = '/learning-budgets/setups';

  static async getLearningBudgetTransactionsWithDetail(
    filters?: Omit<ILearningBudgetTransactionFilters, 'digest'>,
    pagination?: ApiPaginationParams,
  ): Promise<ApiPaginatedResponse<ILearningBudgetTransactionInTheList>> {
    const queryParams = qs.stringify(
      {
        ...filters,
        ...pagination,
        include: [
          'order',
          'course',
          'user',
          'educationProvider',
          'profile',
          'team',
        ],
      },
      { arrayFormat: 'none' },
    );

    return ApiService.get(
      `${LearningBudgetService.TRANSACTIONS_PATH}?${queryParams}`,
    );
  }

  static async getLearningBudgetTransactionsDigest(
    params: ApiPaginationParams & ILearningBudgetTransactionFilters,
  ): Promise<ApiLearningBudgetTransactionsDigestResponse> {
    const queryParams = qs.stringify({
      ...params,
      digest: true,
    });

    return ApiService.get(
      `${LearningBudgetService.TRANSACTIONS_PATH}?${queryParams}`,
    );
  }

  static async updateUserBudgetAmount({
    user,
    amount,
  }: UpdateUserBudgetAmountOpts) {
    return ApiService.post(LearningBudgetService.PATH, {
      user,
      amount,
    });
  }

  static async updateTeamBudgetAmount({
    team,
    amount,
  }: UpdateTeamBudgetAmountOpts) {
    return ApiService.post(LearningBudgetService.PATH, {
      team,
      amount,
    });
  }

  static async exportLearningBudgetTransactions(
    params: ApiPaginationParams & ILearningBudgetTransactionFilters,
  ): Promise<Response> {
    return await ApiService.get(
      `${LearningBudgetService.TRANSACTIONS_EXPORT_PATH}?${qs.stringify(
        {
          ...params,
          include: [
            'order',
            'course',
            'user',
            'educationProvider',
            'profile',
            'team',
          ],
        },
        { arrayFormat: 'none' },
      )}`,
      {
        returnRawResponse: true,
        headers: {
          Accept: 'text/csv',
        },
      },
    );
  }

  static async getLearningBudgetSetups(
    opts?: GetLearningBudgetSetupFilters &
      ApiPaginationParams & { sort?: ISortingParams },
  ): Promise<ApiPaginatedResponse<ILearningBudgetSetupInTheList>> {
    const { sort, ...rest } = opts ?? {};
    const queryParams = qs.stringify({
      ...rest,
      ...(sort && {
        sort: Object.entries(sort).reduce(
          (acc: string[], [fieldName, sort]) => {
            return [...acc, `${fieldName}[${sort}]`];
          },
          [],
        ),
      }),
    });
    return ApiService.get(`${LearningBudgetService.SETUP_PATH}?${queryParams}`);
  }

  static async createLearningBudgetSetup(
    setup: ILearningBudgetCreatePayload,
  ): Promise<ApiIndividualResponse<ILearningBudgetSetup>> {
    return ApiService.post(LearningBudgetService.SETUP_PATH, setup);
  }

  static async updateLearningBudgetSetup(
    id: string,
    update: ILearningBudgetUpdatePayload,
  ): Promise<ApiIndividualResponse<ILearningBudgetSetup>> {
    return ApiService.patch(
      `${LearningBudgetService.SETUP_PATH}/${id}`,
      update,
    );
  }
}
